import { Autocomplete, FormControl, TextField } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

interface Props {
  label?: string;
  width?: any;
  data: Array<string>;
  value: string;
  disabled?: boolean;
  error?: string;
  name: string;
  inputRef?: React.Ref<HTMLInputElement>;
  onChangeValue: (name: string, value: string) => void;
  onSearchChange?: (value: string) => void;
}

export const CustomAutocompleteAddOption: React.FC<Props> = ({
  width,
  label,
  data,
  value,
  disabled,
  name,
  error,
  inputRef,
  onChangeValue,
  onSearchChange,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentValue, setCurrentValue] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Array<string>>([...data]);

  useEffect(() => {
    setOptions([...data]);
  }, [data]);

  useEffect(() => {
    setCurrentValue(value ?? "");
  }, [value]);

  const getFilteredOptions = useCallback(() => {
    const filtered = options?.filter((option) =>
      option?.toLowerCase().includes(inputValue?.toLowerCase())
    );

    if (
      inputValue &&
      !options.some(
        (option) => option?.toLowerCase() === inputValue?.toLowerCase()
      )
    ) {
      return [...filtered, `Add "${inputValue}"`];
    }

    return filtered;
  }, [inputValue, options]);

  const addValue = useCallback(
    (value: string) => {
      setOptions((prev) => [...prev, value]);
      onChangeValue(name, value);
    },
    [name, onChangeValue]
  );

  return (
    <FormControl fullWidth sx={{ width: width ?? "100%" }}>
      <Autocomplete
        autoHighlight
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        size="small"
        freeSolo
        options={getFilteredOptions()}
        value={currentValue ?? ""}
        inputValue={inputValue ?? ""}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
          onSearchChange?.(newInputValue);
        }}
        onChange={(_event, newValue) => {
          if (typeof newValue === "string" && newValue.startsWith("Add ")) {
            const actualValue = inputValue;
            addValue(actualValue);
            setCurrentValue(actualValue);
          } else {
            onChangeValue(name, newValue || "");
            setCurrentValue(newValue);
          }
        }}
        onBlur={() => {
          if (inputValue && inputValue !== currentValue) {
            onChangeValue(name, inputValue);
            setCurrentValue(inputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            inputRef={inputRef}
            error={!!error}
            helperText={error}
            {...params}
            label={label ?? ""}
            onKeyDown={(e) => {
              const allowedKeys = [
                "ArrowUp",
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight",
                "Enter",
                "Escape",
                "Tab",
              ];
              if (!allowedKeys.includes(e.key)) {
                e.stopPropagation();
              }
            }}
          />
        )}
      />
    </FormControl>
  );
};
